import { type FormEvent, useMemo, useState } from 'react';
import { AlertTriangle, MapPin, Plus, Save, Trash2, UserRound, X } from 'lucide-react';
import { SearchSelect, confirmDiscard } from '@forq/ui';
import type {
  CreateCustomerAddressInput,
  CreateCustomerInput,
  CustomerAddressRecord,
  CustomerContactRecord,
  CustomerDetailRecord,
  CustomerSpecialPart,
  CustomerDuplicateCandidate,
  CustomerDuplicateReason,
  CustomersGateway,
  UpdateCustomerInput,
} from '@forq/customers-data';

/** Gängige Lieferbedingungen (Incoterms 2020 + geläufige deutsche Begriffe) als Vorschläge -- wählbar
 *  oder frei eingebbar. Bewusst deckungsgleich mit der Liste im Vorgangs-Editor (@forq/orders). */
const deliveryTermOptions: readonly string[] = [
  'EXW – Ab Werk Werl', 'FCA – Frei Frachtführer', 'CPT – Frachtfrei', 'CIP – Frachtfrei versichert',
  'DAP – Geliefert benannter Ort (frei Haus)', 'DPU – Geliefert entladen', 'DDP – Geliefert verzollt',
  'frei Haus', 'ab Werk', 'Selbstabholung', 'unfrei',
];

/** Lieferbedingungen als Dropdown mit „Sonstiges …" -> Freitextfeld (deckungsgleich mit dem Vorgang). */
function DeliveryTermsField({ value: current, onChange }: { readonly value: string; readonly onChange: (next: string) => void }) {
  const isKnown = deliveryTermOptions.includes(current);
  const [customMode, setCustomMode] = useState(!isKnown && current !== '');
  const showCustom = customMode || (!isKnown && current !== '');
  return (
    <>
      <select
        value={showCustom ? '__custom__' : current}
        onChange={(event) => {
          if (event.target.value === '__custom__') { setCustomMode(true); onChange(''); }
          else { setCustomMode(false); onChange(event.target.value); }
        }}
      >
        <option value="">— keine —</option>
        {deliveryTermOptions.map((term) => <option key={term} value={term}>{term}</option>)}
        <option value="__custom__">Sonstiges (eigene eingeben) …</option>
      </select>
      {showCustom ? (
        <input value={current} onChange={(event) => onChange(event.target.value)} placeholder="Eigene Lieferbedingung eingeben" />
      ) : null}
    </>
  );
}

function value(form: FormData, key: string): string {
  return String(form.get(key) ?? '').trim();
}

function reasonLabel(reason: CustomerDuplicateReason): string {
  if (reason === 'vat-id') return 'gleiche USt-ID';
  if (reason === 'email') return 'gleiche E-Mail';
  if (reason === 'phone') return 'gleiche Telefonnummer';
  if (reason === 'name-postal-code') return 'gleicher Name und gleiche PLZ';
  if (reason === 'name-city') return 'gleicher Name und gleicher Ort';
  return 'gleicher Name';
}

type CustomerSaveInput = CreateCustomerInput | UpdateCustomerInput;

interface CustomerDialogProps {
  readonly mode: 'create' | 'edit';
  readonly tenantId: string;
  readonly gateway: CustomersGateway;
  readonly customer?: CustomerDetailRecord;
  readonly duplicateCheckEnabled: boolean;
  readonly onClose: () => void;
  readonly onSaved: (customer: CustomerDetailRecord) => void;
  readonly onChooseCandidate: (customerId: string) => void;
}

export function CustomerDialog({
  mode,
  tenantId,
  gateway,
  customer,
  duplicateCheckEnabled,
  onClose,
  onSaved,
  onChooseCandidate,
}: CustomerDialogProps) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<readonly CustomerDuplicateCandidate[]>([]);
  const [pendingInput, setPendingInput] = useState<CustomerSaveInput | null>(null);
  const primaryAddress = useMemo(
    () => customer?.addresses.find((address) => address.kind === 'billing' && address.primary)
      ?? customer?.addresses.find((address) => address.primary)
      ?? customer?.addresses[0],
    [customer],
  );
  const hasBlockingVatDuplicate = duplicates.some((candidate) => candidate.reasons.includes('vat-id'));

  async function persist(input: CustomerSaveInput): Promise<void> {
    setSaving(true);
    setSaveError(null);
    try {
      const saved = 'customerId' in input
        ? await gateway.updateCustomer(input)
        : await gateway.createCustomer(input);
      onSaved(saved);
    } catch (error: unknown) {
      setSaveError(error instanceof Error ? error.message : 'Kunde konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const kind = value(form, 'kind') === 'person' ? 'person' : 'company';
    const common = {
      tenantId,
      kind,
      companyName: value(form, 'companyName'),
      firstName: value(form, 'firstName'),
      lastName: value(form, 'lastName'),
      vatId: value(form, 'vatId'),
      taxNumber: value(form, 'taxNumber'),
      email: value(form, 'email'),
      phone: value(form, 'phone'),
      website: value(form, 'website'),
      notes: value(form, 'notes'),
    } as const;
    const input: CustomerSaveInput = mode === 'edit' && customer
      ? {
          ...common,
          customerId: customer.id,
          status: value(form, 'status') === 'blocked'
            ? 'blocked'
            : value(form, 'status') === 'prospect'
              ? 'prospect'
              : 'active',
        }
      : {
          ...common,
          billingAddress: value(form, 'city') ? {
            street: value(form, 'street'),
            postalCode: value(form, 'postalCode'),
            city: value(form, 'city'),
            countryCode: value(form, 'countryCode') || 'DE',
          } : undefined,
        };

    if (!duplicateCheckEnabled) {
      await persist(input);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const candidates = await gateway.findDuplicates({
        tenantId,
        customerId: mode === 'edit' ? customer?.id : undefined,
        kind,
        companyName: common.companyName,
        firstName: common.firstName,
        lastName: common.lastName,
        vatId: common.vatId,
        email: common.email,
        phone: common.phone,
        postalCode: mode === 'create' ? value(form, 'postalCode') : primaryAddress?.postalCode,
        city: mode === 'create' ? value(form, 'city') : primaryAddress?.city,
      });
      if (candidates.length > 0) {
        setDuplicates(candidates);
        setPendingInput(input);
        return;
      }
      await persist(input);
    } catch (error: unknown) {
      setSaveError(error instanceof Error ? error.message : 'Dublettenprüfung konnte nicht ausgeführt werden.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="customers-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && confirmDiscard()) onClose(); }}>
      <form className="customers-modal__panel" onSubmit={(event) => void submit(event)}>
        <div className="customers-dialog-title">
          <div>
            <span className="customers-eyebrow">{mode === 'create' ? 'Neuer Kunde' : `Kunde ${customer?.number ?? ''}`}</span>
            <h2>{mode === 'create' ? 'Kundenstamm anlegen' : 'Kundenstamm bearbeiten'}</h2>
            <p>{mode === 'create' ? 'Die Kundennummer wird automatisch vergeben.' : 'Änderungen gelten nur für den aktiven Mandanten.'}</p>
          </div>
          <button className="customers-icon-button" type="button" onClick={onClose} aria-label="Dialog schließen"><X size={18} /></button>
        </div>

        <div className="customers-form-grid">
          <label>Kundenart<SearchSelect name="kind" defaultValue={customer?.kind ?? 'company'} options={[{ value: 'company', label: 'Unternehmen' }, { value: 'person', label: 'Privatperson' }]} /></label>
          {mode === 'edit' ? <label>Status<SearchSelect name="status" defaultValue={customer?.status ?? 'active'} options={[{ value: 'active', label: 'Aktiv' }, { value: 'prospect', label: 'Interessent' }, { value: 'blocked', label: 'Gesperrt' }]} /></label> : null}
          <label>Firmenname<input name="companyName" defaultValue={customer?.companyName ?? ''} /></label>
          <label>Vorname<input name="firstName" defaultValue={customer?.firstName ?? ''} /></label>
          <label>Nachname<input name="lastName" defaultValue={customer?.lastName ?? ''} /></label>
          <label>USt-ID<input name="vatId" defaultValue={customer?.vatId ?? ''} placeholder="DE…" /></label>
          <label>Steuernummer<input name="taxNumber" defaultValue={customer?.taxNumber ?? ''} /></label>
          <label>Telefon<input name="phone" defaultValue={customer?.phone ?? ''} /></label>
          <label>E-Mail<input name="email" type="email" defaultValue={customer?.email ?? ''} /></label>
          <label>Website<input name="website" defaultValue={customer?.website ?? ''} /></label>
          {mode === 'create' ? (
            <>
              <label>Straße<input name="street" /></label>
              <label>PLZ<input name="postalCode" /></label>
              <label>Ort<input name="city" /></label>
              <label>Ländercode<input name="countryCode" defaultValue="DE" maxLength={2} /></label>
            </>
          ) : null}
          <label className="is-wide">Notizen<textarea name="notes" rows={4} defaultValue={customer?.notes ?? ''} /></label>
        </div>

        {duplicates.length > 0 ? (
          <section className="customers-duplicates" aria-live="polite">
            <div className="customers-duplicates__heading"><AlertTriangle size={18} /><div><strong>Mögliche Dubletten gefunden</strong><span>Bitte prüfe bestehende Kunden vor dem Speichern.</span></div></div>
            <div className="customers-duplicates__list">
              {duplicates.map((candidate) => (
                <button type="button" key={candidate.id} onClick={() => onChooseCandidate(candidate.id)}>
                  <span><strong>{candidate.displayName}</strong><small>{candidate.number} · {candidate.city || 'Ort fehlt'}</small></span>
                  <span>{candidate.score}%<small>{candidate.reasons.map(reasonLabel).join(', ')}</small></span>
                </button>
              ))}
            </div>
            {hasBlockingVatDuplicate ? <p>Eine identische USt-ID ist bereits vorhanden. Dieser Datensatz kann nicht zusätzlich gespeichert werden.</p> : null}
          </section>
        ) : null}

        {saveError ? <div className="customers-modal__error" role="alert">{saveError}</div> : null}
        <div className="customers-modal__actions">
          <button type="button" className="customers-secondary" onClick={onClose}>Abbrechen</button>
          {pendingInput && !hasBlockingVatDuplicate ? (
            <button type="button" className="customers-warning-button" disabled={saving} onClick={() => void persist(pendingInput)}>
              <AlertTriangle size={16} /> Trotzdem speichern
            </button>
          ) : (
            <button type="submit" className="customers-primary" disabled={saving || hasBlockingVatDuplicate}>
              <Save size={16} /> {saving ? 'Prüft und speichert …' : mode === 'create' ? 'Kunde anlegen' : 'Änderungen speichern'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

interface AddressDialogProps {
  readonly tenantId: string;
  readonly customerId: string;
  readonly gateway: CustomersGateway;
  readonly address?: CustomerAddressRecord;
  readonly onClose: () => void;
  readonly onSaved: (customer: CustomerDetailRecord) => void;
  readonly onError: (message: string) => void;
}

export function AddressDialog({ tenantId, customerId, gateway, address, onClose, onSaved, onError }: AddressDialogProps) {
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const kind = value(form, 'kind') === 'delivery' ? 'delivery' : value(form, 'kind') === 'site' ? 'site' : 'billing';
    const input: CreateCustomerAddressInput = {
      tenantId,
      customerId,
      kind,
      label: value(form, 'label'),
      street: value(form, 'street'),
      postalCode: value(form, 'postalCode'),
      city: value(form, 'city'),
      countryCode: value(form, 'countryCode') || 'DE',
      primary: form.get('primary') === 'on',
    };
    setSaving(true);
    try {
      const saved = address
        ? await gateway.updateAddress({ ...input, addressId: address.id })
        : await gateway.createAddress(input);
      onSaved(saved);
    } catch (error: unknown) {
      onError(error instanceof Error ? error.message : 'Anschrift konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="customers-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && confirmDiscard()) onClose(); }}>
      <form className="customers-modal__panel is-small" onSubmit={(event) => void submit(event)}>
        <div className="customers-dialog-title"><div><span className="customers-eyebrow">Anschriften</span><h2>{address ? 'Anschrift bearbeiten' : 'Anschrift hinzufügen'}</h2></div><button className="customers-icon-button" type="button" onClick={onClose}><X size={18} /></button></div>
        <div className="customers-form-grid">
          <label>Art<SearchSelect name="kind" defaultValue={address?.kind ?? 'billing'} options={[{ value: 'billing', label: 'Rechnungsanschrift' }, { value: 'delivery', label: 'Lieferanschrift' }, { value: 'site', label: 'Einsatzort' }]} /></label>
          <label>Bezeichnung<input name="label" defaultValue={address?.label ?? ''} /></label>
          <label className="is-wide">Straße<input name="street" defaultValue={address?.street ?? ''} /></label>
          <label>PLZ<input name="postalCode" defaultValue={address?.postalCode ?? ''} /></label>
          <label>Ort<input name="city" required defaultValue={address?.city ?? ''} /></label>
          <label>Ländercode<input name="countryCode" maxLength={2} defaultValue={address?.countryCode ?? 'DE'} /></label>
          <label className="customers-check"><input name="primary" type="checkbox" defaultChecked={address?.primary ?? false} /> Hauptanschrift dieser Art</label>
        </div>
        <div className="customers-modal__actions"><button type="button" className="customers-secondary" onClick={onClose}>Abbrechen</button><button className="customers-primary" type="submit" disabled={saving}><MapPin size={16} /> {saving ? 'Speichert …' : 'Anschrift speichern'}</button></div>
      </form>
    </div>
  );
}

interface AgreementsDialogProps {
  readonly tenantId: string;
  readonly customer: CustomerDetailRecord;
  readonly gateway: CustomersGateway;
  readonly onClose: () => void;
  readonly onSaved: (customer: CustomerDetailRecord) => void;
  readonly onError: (message: string) => void;
}

export function AgreementsDialog({ tenantId, customer, gateway, onClose, onSaved, onError }: AgreementsDialogProps) {
  const [debtorNumber, setDebtorNumber] = useState(customer.agreements.debtorNumber ?? '');
  const [leitwegId, setLeitwegId] = useState(customer.agreements.leitwegId ?? '');
  const [dunningBlocked, setDunningBlocked] = useState(customer.agreements.dunningBlocked === true);
  const [dunningBlockReason, setDunningBlockReason] = useState(customer.agreements.dunningBlockReason ?? '');
  const [creditLimit, setCreditLimit] = useState(String(customer.agreements.creditLimit ?? 0));
  const [discountPercent, setDiscountPercent] = useState(String(customer.agreements.discountPercent));
  const [paymentTermDays, setPaymentTermDays] = useState(String(customer.agreements.paymentTermDays));
  const [bankName, setBankName] = useState(customer.agreements.bankName ?? '');
  const [iban, setIban] = useState(customer.agreements.iban ?? '');
  const [bic, setBic] = useState(customer.agreements.bic ?? '');
  const [conditions, setConditions] = useState(customer.agreements.conditions);
  const [deliveryTerms, setDeliveryTerms] = useState(customer.agreements.deliveryTerms ?? '');
  const [parts, setParts] = useState<CustomerSpecialPart[]>(customer.agreements.specialParts.map((part) => ({ ...part })));
  const [saving, setSaving] = useState(false);

  function patchPart(id: string, patch: Partial<CustomerSpecialPart>): void {
    setParts((current) => current.map((part) => (part.id === id ? { ...part, ...patch } : part)));
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSaving(true);
    try {
      const saved = await gateway.updateAgreements({
        tenantId,
        customerId: customer.id,
        agreements: {
          debtorNumber,
          leitwegId: leitwegId.trim(),
          dunningBlocked,
          dunningBlockReason: dunningBlockReason.trim(),
          creditLimit: Number(creditLimit) || 0,
          discountPercent: Number(discountPercent) || 0,
          paymentTermDays: Number(paymentTermDays) || 0,
          conditions,
          deliveryTerms,
          bankName,
          iban,
          bic,
          specialParts: parts,
        },
      });
      onSaved(saved);
    } catch (error: unknown) {
      onError(error instanceof Error ? error.message : 'Sondervereinbarungen konnten nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="customers-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && confirmDiscard()) onClose(); }}>
      <form className="customers-modal__panel" onSubmit={(event) => void submit(event)}>
        <div className="customers-dialog-title"><div><span className="customers-eyebrow">Konditionen & Buchhaltung</span><h2>{customer.displayName}</h2></div><button className="customers-icon-button" type="button" onClick={onClose}><X size={18} /></button></div>
        <div className="customers-form-grid">
          <label>Debitorennummer<input value={debtorNumber} onChange={(event) => setDebtorNumber(event.target.value)} placeholder="z. B. 10001" /></label>
          <label>Leitweg-ID <small>(nur öffentliche Auftraggeber)</small>
            <input value={leitwegId} onChange={(event) => setLeitwegId(event.target.value)} placeholder="z. B. 991-01234-56" />
          </label>
          <label className="customers-modal__check">
            <input type="checkbox" checked={dunningBlocked} onChange={(event) => setDunningBlocked(event.target.checked)} />
            Mahnsperre <small>(dieser Kunde wird nicht gemahnt)</small>
          </label>
          {dunningBlocked && (
            <label>Grund der Mahnsperre
              <input value={dunningBlockReason} onChange={(event) => setDunningBlockReason(event.target.value)} placeholder="z. B. Reklamation in Klärung" />
            </label>
          )}
          <label>Kreditlimit (EUR)<input type="number" min={0} step="0.01" value={creditLimit} onChange={(event) => setCreditLimit(event.target.value)} /></label>
          <label>Rabatt %<input type="number" min={0} max={100} step="0.01" value={discountPercent} onChange={(event) => setDiscountPercent(event.target.value)} /></label>
          <label>Zahlungsziel (Tage)<input type="number" min={0} value={paymentTermDays} onChange={(event) => setPaymentTermDays(event.target.value)} /></label>
          <label>Lieferbedingungen (Incoterms)<DeliveryTermsField value={deliveryTerms} onChange={setDeliveryTerms} /></label>
          <label>Bank<input value={bankName} onChange={(event) => setBankName(event.target.value)} placeholder="Institut" /></label>
          <label>IBAN<input value={iban} onChange={(event) => setIban(event.target.value)} placeholder="DE.." /></label>
          <label>BIC<input value={bic} onChange={(event) => setBic(event.target.value)} /></label>
          <label className="is-wide">Konditionen / Nachlässe<textarea rows={3} value={conditions} onChange={(event) => setConditions(event.target.value)} placeholder="z. B. Rahmenvereinbarung, Frachtkonditionen, Sonderabsprachen …" /></label>
        </div>
        <div className="customers-parts">
          <div className="customers-section__heading"><div><span className="customers-eyebrow">Kundenspezifisch</span><h3>Sonder-Ersatzteile</h3></div><button type="button" className="customers-secondary" onClick={() => setParts((current) => [...current, { id: crypto.randomUUID(), partNumber: '', description: '', note: '' }])}><Plus size={15} /> Ersatzteil</button></div>
          {parts.map((part) => (
            <div key={part.id} className="customers-part-row">
              <input placeholder="Teile-Nr." value={part.partNumber} onChange={(event) => patchPart(part.id, { partNumber: event.target.value })} />
              <input placeholder="Bezeichnung" value={part.description} onChange={(event) => patchPart(part.id, { description: event.target.value })} />
              <input placeholder="Notiz" value={part.note} onChange={(event) => patchPart(part.id, { note: event.target.value })} />
              <button type="button" className="customers-icon-button" onClick={() => setParts((current) => current.filter((item) => item.id !== part.id))}><Trash2 size={15} /></button>
            </div>
          ))}
          {parts.length === 0 ? <p className="customers-empty-hint">Keine Sonder-Ersatzteile hinterlegt.</p> : null}
        </div>
        <div className="customers-modal__actions"><button type="button" className="customers-secondary" onClick={onClose}>Abbrechen</button><button className="customers-primary" type="submit" disabled={saving}><Save size={16} /> {saving ? 'Speichert …' : 'Speichern'}</button></div>
      </form>
    </div>
  );
}

interface ContactDialogProps {
  readonly tenantId: string;
  readonly customerId: string;
  readonly gateway: CustomersGateway;
  readonly contact?: CustomerContactRecord;
  readonly onClose: () => void;
  readonly onSaved: (customer: CustomerDetailRecord) => void;
  readonly onError: (message: string) => void;
}

export function ContactDialog({ tenantId, customerId, gateway, contact, onClose, onSaved, onError }: ContactDialogProps) {
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const common = {
      tenantId,
      customerId,
      salutation: value(form, 'salutation'),
      firstName: value(form, 'firstName'),
      lastName: value(form, 'lastName'),
      position: value(form, 'position'),
      email: value(form, 'email'),
      phone: value(form, 'phone'),
      mobile: value(form, 'mobile'),
      primary: form.get('primary') === 'on',
    } as const;
    setSaving(true);
    try {
      const saved = contact
        ? await gateway.updateContact({ ...common, contactId: contact.id, active: form.get('active') === 'on' })
        : await gateway.createContact(common);
      onSaved(saved);
    } catch (error: unknown) {
      onError(error instanceof Error ? error.message : 'Ansprechpartner konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="customers-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && confirmDiscard()) onClose(); }}>
      <form className="customers-modal__panel is-small" onSubmit={(event) => void submit(event)}>
        <div className="customers-dialog-title"><div><span className="customers-eyebrow">Ansprechpartner</span><h2>{contact ? 'Ansprechpartner bearbeiten' : 'Ansprechpartner anlegen'}</h2></div><button className="customers-icon-button" type="button" onClick={onClose}><X size={18} /></button></div>
        <div className="customers-form-grid">
          <label>Anrede<input name="salutation" defaultValue={contact?.salutation ?? ''} /></label>
          <label>Position<input name="position" defaultValue={contact?.position ?? ''} /></label>
          <label>Vorname<input name="firstName" defaultValue={contact?.firstName ?? ''} /></label>
          <label>Nachname<input name="lastName" required defaultValue={contact?.lastName ?? ''} /></label>
          <label>E-Mail<input name="email" type="email" defaultValue={contact?.email ?? ''} /></label>
          <label>Telefon<input name="phone" defaultValue={contact?.phone ?? ''} /></label>
          <label>Mobil<input name="mobile" defaultValue={contact?.mobile ?? ''} /></label>
          <label className="customers-check"><input name="primary" type="checkbox" defaultChecked={contact?.primary ?? false} /> Hauptkontakt</label>
          {contact ? <label className="customers-check"><input name="active" type="checkbox" defaultChecked={contact.active} /> Ansprechpartner aktiv</label> : null}
        </div>
        <div className="customers-modal__actions"><button type="button" className="customers-secondary" onClick={onClose}>Abbrechen</button><button className="customers-primary" type="submit" disabled={saving}><UserRound size={16} /> {saving ? 'Speichert …' : 'Ansprechpartner speichern'}</button></div>
      </form>
    </div>
  );
}
